/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Universal Shared DTOs for Admin Feature Flags & Maintenance Mode (Task 11.5.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Standard Feature Flag Item Schema
 */
export const FeatureFlagItemSchema = z
  .object({
    key: z
      .string()
      .min(1)
      .openapi({ example: 'maintenance_mode', description: 'Unique slug identifier for the feature flag' }),
    name: z
      .string()
      .min(1)
      .openapi({ example: 'Emergency Maintenance Mode', description: 'Human-readable name of the feature' }),
    description: z
      .string()
      .openapi({ example: 'Temporarily restricts non-admin access', description: 'Detailed explanation of what the flag gates' }),
    category: z
      .enum(['system', 'identity', 'ai', 'media', 'voice', 'billing', 'growth'])
      .openapi({ example: 'system', description: 'Functional architectural category' }),
    isEnabled: z
      .boolean()
      .openapi({ example: false, description: 'Current runtime state of the feature flag' }),
    isSystemCritical: z
      .boolean()
      .default(false)
      .openapi({ example: true, description: 'Whether toggling this flag impacts overall platform availability' }),
    updatedAt: z
      .string()
      .openapi({ example: '2026-10-26T14:00:00.000Z', description: 'Last modified ISO timestamp' }),
  })
  .openapi('FeatureFlagItem');

export type FeatureFlagItem = z.infer<typeof FeatureFlagItemSchema>;

/**
 * List of Feature Flags Response Schema
 */
export const FeatureFlagListSchema = z.array(FeatureFlagItemSchema).openapi('FeatureFlagList');

/**
 * Toggle Single Feature Flag Request Schema
 */
export const ToggleFeatureFlagSchema = z
  .object({
    key: z
      .string()
      .min(1)
      .openapi({ example: 'maintenance_mode', description: 'Target feature flag key to toggle' }),
    isEnabled: z
      .boolean()
      .openapi({ example: true, description: 'New boolean state for the feature flag' }),
  })
  .openapi('ToggleFeatureFlagInput');

export type ToggleFeatureFlagInput = z.infer<typeof ToggleFeatureFlagSchema>;
